import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { ErpTable, EmptyRow, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th }
  from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtDate } from '@/shared/lib/format'
import { RankedBars, ChartFrame } from '@/shared/ui/charts'
import { Donut } from '@/shared/charts/Donut'
import type { SurveyResponseDto } from '@/shared/api/types'
import { listPolls } from './api'
import { Pill, categoryLabel, count, pollStatusLabel, pollStatusTone } from './vocab'

/**
 * The Polling Studio dashboard — the portfolio, and what needs a decision today.
 *
 * ## What this screen still refuses to do
 *
 * PollSphere's overview leads with a "prediction readiness" score in the seventies,
 * computed from a fabricated formula over whatever happened to be in the database. A single
 * composite at the top of a module is a claim that everything below it rolls up, and
 * nothing here does: a poll in the field, one awaiting review and one closed last month are
 * three situations for three different people. There is still no score, and there will not
 * be one.
 *
 * There is also still no averaged response rate. A rate averaged across polls with different
 * instruments and different populations is a number nobody can act on, and putting it in a
 * tile would make it look like one.
 *
 * ## What it now does instead
 *
 * The old version counted and listed, which is honest and not much use: a manager opening
 * this screen wants to know which poll needs them, and counting drafts does not say. So the
 * page derives exactly one thing, from two fields every poll already carries — `collectedCount`
 * against `targetSample`, and where the poll is between `opensAt` and `closesAt`. That gives
 * a per-poll pace, which is checkable by looking at the row underneath it, and which is the
 * one comparison that IS legitimate across polls: a poll is behind against its own target,
 * not against another poll's.
 *
 * Everything else on the page is a count, a share, or a link to the screen that owns the
 * detail.
 */
export function PollingOverviewPage() {
  const canRead = useCan('survey:read')
  const polls = useLoad(() => listPolls({}, 0, 100), [])
  const [only, setOnly] = useState<string | null>(null)

  const rows = polls.data?.content ?? []
  const view = useMemo(
    () => (only ? rows.filter((p) => p.statusCode === only) : rows), [rows, only])

  const live = rows.filter((p) => p.statusCode === 'LIVE')
  const paced = useMemo(() => live.map(pace).sort((a, b) => a.pace - b.pace), [live])
  const attention = useMemo(() => needsAttention(rows, paced), [rows, paced])

  if (!canRead) return <Forbidden what="the Polling Studio" />

  const byStatus = (code: string) => rows.filter((p) => p.statusCode === code).length
  const targeted = live.reduce((sum, p) => sum + p.targetSample, 0)
  const collected = live.reduce((sum, p) => sum + p.collectedCount, 0)

  const statusCounts = new Map<string, number>()
  for (const p of rows) statusCounts.set(p.statusCode, (statusCounts.get(p.statusCode) ?? 0) + 1)
  const categoryCounts = new Map<string, number>()
  for (const p of rows) {
    categoryCounts.set(p.categoryCode, (categoryCounts.get(p.categoryCode) ?? 0) + 1)
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Polling Studio"
        description="The portfolio, what is in the field, and which poll needs a decision today."
      />

      <ErrorStrip message={polls.error} />

      {polls.loading && rows.length === 0 ? <LoadingBlock label="Loading polls…" /> : (
        <>
          <KpiStrip>
            <MetricCard label="In the field" value={count(live.length)} sub="collecting now" />
            <MetricCard
              label="Interviews collected"
              value={count(collected)}
              sub={targeted > 0
                ? `${Math.round((100 * collected) / targeted)}% of ${count(targeted)} targeted`
                : 'nothing in the field'}
            />
            <MetricCard
              label="Behind schedule"
              value={count(paced.filter((p) => p.pace < -0.1).length)}
              sub={live.length === 0 ? 'no live polls' : `of ${live.length} live`}
              accent={paced.some((p) => p.pace < -0.1)}
            />
            <MetricCard label="Awaiting review" value={count(byStatus('IN_REVIEW'))}
              sub="somebody else has to approve" accent={byStatus('IN_REVIEW') > 0} />
            <MetricCard label="Drafts" value={count(byStatus('DRAFT'))} sub="not yet submitted" />
            <MetricCard label="Closed" value={count(byStatus('CLOSED') + byStatus('ARCHIVED'))}
              sub="fieldwork finished" />
          </KpiStrip>

          {attention.length > 0 && (
            <Panel
              title="Needs a decision"
              description="Derived from each poll's own target and its own schedule — never from a comparison between polls."
              bodyClassName="p-0"
            >
              <ul className="divide-y divide-border">
                {attention.map((a) => (
                  <li key={a.id} className={cn('p-3 border-l-[3px]', TONE_RING[a.tone])}>
                    <div className="flex flex-wrap items-baseline gap-2">
                      <Link to={`/polling/polls?poll=${a.id}`}
                            className="font-mono text-erp-xs text-chocolate hover:underline">
                        {a.ref}
                      </Link>
                      <span className="text-erp-sm font-medium">{a.headline}</span>
                    </div>
                    <p className="text-erp-xs text-muted-foreground mt-0.5">{a.detail}</p>
                    <p className="text-erp-sm mt-1">{a.action}</p>
                  </li>
                ))}
              </ul>
            </Panel>
          )}

          {/* Three columns rather than two: the fieldwork panel holds one short bar per
              live poll, and stretched to the height of two stacked donuts it was mostly
              empty space. */}
          <div className="grid gap-4 lg:grid-cols-3 items-start">
            <Panel
              title="Fieldwork against target"
              description="Each live poll against its OWN target. The only cross-poll comparison this page makes is of that ratio."
            >
              <ChartFrame title="Collected, % of target">
                <RankedBars
                  points={paced.map((p) => ({
                    label: p.ref,
                    value: Math.round(p.share * 100),
                    hint: `${count(p.poll.collectedCount)} of ${count(p.poll.targetSample)}`
                      + (p.elapsed != null ? ` · ${Math.round(p.elapsed * 100)}% of the field period gone` : ''),
                  }))}
                  max={100}
                  tone={(label) => {
                    const p = paced.find((it) => it.ref === label)
                    if (!p) return undefined
                    return p.pace < -0.1 ? 'bad' : p.pace > 0.05 ? 'good' : 'warn'
                  }}
                  formatValue={(n) => `${n}%`}
                  emptyMessage="No poll is in the field."
                />
              </ChartFrame>
              {paced.length > 0 && (
                <p className="mt-3 text-erp-xs text-muted-foreground">
                  Colour is pace, not size: a poll is red when the share collected trails the
                  share of its field period that has gone. A poll with no close date has no
                  pace and is shown neutral — there is nothing to be behind.
                </p>
              )}
            </Panel>

            <div className="contents">
              <Panel title="Portfolio by status"
                     description="Where the polls are in the state machine. Click a slice's row below to filter the table.">
                <Donut
                  slices={[...statusCounts].map(([code, n]) => ({
                    id: code, label: pollStatusLabel(code), value: n,
                  }))}
                  total={rows.length}
                  centreLabel="Polls"
                  formatValue={(v) => `${Math.round(v)}`}
                  emptyMessage="No polls yet — nothing to divide."
                />
              </Panel>
              <Panel title="Portfolio by kind"
                     description="What the studio is actually being used for.">
                <Donut
                  slices={[...categoryCounts].map(([code, n]) => ({
                    id: code, label: categoryLabel(code), value: n,
                  }))}
                  total={rows.length}
                  centreLabel="Polls"
                  formatValue={(v) => `${Math.round(v)}`}
                  emptyMessage="No polls yet — nothing to divide."
                />
              </Panel>
            </div>
          </div>

          <Panel
            title="Polls"
            description="Newest first. A poll's status is its position in the state machine, not a label somebody typed."
            right={
              <select
                value={only ?? ''}
                onChange={(e) => setOnly(e.target.value || null)}
                className="rounded-md border border-border bg-background px-2 py-1.5 text-erp-sm"
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {[...statusCounts.keys()].map((code) => (
                  <option key={code} value={code}>{pollStatusLabel(code)}</option>
                ))}
              </select>
            }
            bodyClassName="p-0"
          >
            <ErpTable head={(
              <>
                <Th>Ref</Th>
                <Th>Title</Th>
                <Th>Status</Th>
                <Th>Category</Th>
                <Th>Scope</Th>
                <Th className="text-right">Collected</Th>
                <Th className="text-right">Target</Th>
                <Th>Progress</Th>
                <Th>Opens</Th>
              </>
            )}>
              {view.length === 0 ? (
                <EmptyRow colSpan={9} message={
                  only
                    ? 'No poll has that status.'
                    : 'No polls yet. Create one from the Polls screen — a poll starts as a draft '
                      + 'with an empty instrument, and nothing is published until somebody says so.'
                } />
              ) : view.map((poll) => {
                const share = poll.targetSample > 0
                  ? Math.min(poll.collectedCount / poll.targetSample, 1) : 0
                return (
                  <tr key={poll.id} className="hover:bg-muted/40">
                    <Td className="font-mono text-erp-xs">
                      <Link className="text-chocolate hover:underline"
                        to={`/polling/polls?poll=${poll.id}`}>
                        {poll.ref}
                      </Link>
                    </Td>
                    <Td>{poll.title}</Td>
                    <Td>
                      <Pill tone={pollStatusTone(poll.statusCode)}>
                        {pollStatusLabel(poll.statusCode)}
                      </Pill>
                    </Td>
                    <Td className="text-muted-foreground">{categoryLabel(poll.categoryCode)}</Td>
                    <Td className="text-muted-foreground">{poll.scopeUnitName ?? '—'}</Td>
                    <Td className="text-right tabular-nums">{count(poll.collectedCount)}</Td>
                    <Td className="text-right tabular-nums">{count(poll.targetSample)}</Td>
                    <Td>
                      <span className="flex items-center gap-2">
                        <span className="h-1.5 w-20 rounded bg-muted overflow-hidden">
                          <span className="block h-full rounded bg-chocolate"
                                style={{ width: `${share * 100}%` }} />
                        </span>
                        <span className="tabular-nums text-erp-xs text-muted-foreground">
                          {poll.targetSample > 0 ? `${Math.round(share * 100)}%` : '—'}
                        </span>
                      </span>
                    </Td>
                    <Td className="text-muted-foreground">
                      {poll.opensAt ? fmtDate(poll.opensAt) : '—'}
                    </Td>
                  </tr>
                )
              })}
            </ErpTable>
          </Panel>

          <Note tone="info">
            Response rate, quota fill and flag load are on the Fieldwork and Quality screens,
            per poll. They are still not summarised here: a rate averaged across polls with
            different instruments and different populations is a number nobody can act on.
          </Note>
        </>
      )}
    </div>
  )
}

const TONE_RING: Record<string, string> = {
  bad: 'border-l-rose-600',
  warn: 'border-l-amber-500',
  info: 'border-l-sky-500',
}

interface Paced {
  id: string
  ref: string
  poll: SurveyResponseDto
  /** Collected ÷ target, 0..1. */
  share: number
  /** Share of the field period gone, 0..1. Null when the poll has no close date. */
  elapsed: number | null
  /** `share − elapsed`. Negative is behind. Zero when there is no schedule to be behind. */
  pace: number
}

/**
 * Where one poll is against its own plan.
 *
 * Both halves are shares of that poll's own quantities, which is what makes the comparison
 * legal: 60% of target with 80% of the field period gone means the same thing whether the
 * target was 400 or 4,000. A poll with no `closesAt` has no field period and therefore no
 * pace — it is returned at zero rather than as "on track", because "we never set a
 * deadline" and "we are on schedule" are different statements.
 */
function pace(poll: SurveyResponseDto): Paced {
  const share = poll.targetSample > 0
    ? Math.min(poll.collectedCount / poll.targetSample, 1) : 0
  const opens = poll.launchedAt ?? poll.opensAt
  let elapsed: number | null = null
  if (opens && poll.closesAt) {
    const from = new Date(opens).getTime()
    const to = new Date(poll.closesAt).getTime()
    const span = to - from
    if (span > 0) elapsed = Math.max(0, Math.min(1, (Date.now() - from) / span))
  }
  return {
    id: poll.id, ref: poll.ref, poll, share, elapsed,
    pace: elapsed == null ? 0 : share - elapsed,
  }
}

interface Attention {
  id: string
  ref: string
  tone: 'bad' | 'warn' | 'info'
  headline: string
  detail: string
  action: string
}

/**
 * The short list, in the order somebody should work it.
 *
 * Behind-schedule fieldwork first, because it is the only item here that gets worse while
 * nobody looks at it. Review queues and open-ended live polls are process, not decay.
 */
function needsAttention(rows: SurveyResponseDto[], paced: Paced[]): Attention[] {
  const out: Attention[] = []

  for (const p of paced.filter((it) => it.pace < -0.1)) {
    const remaining = p.poll.targetSample - p.poll.collectedCount
    out.push({
      id: p.id, ref: p.ref, tone: p.pace < -0.25 ? 'bad' : 'warn',
      headline: `${p.poll.title} is behind its field schedule.`,
      detail: `${count(p.poll.collectedCount)} of ${count(p.poll.targetSample)} collected `
        + `(${Math.round(p.share * 100)}%) with ${Math.round((p.elapsed ?? 0) * 100)}% of the field period gone.`,
      action: `Either add interviewers now — ${count(remaining)} interviews remain — or move the close date. `
        + 'Publishing against a base the design never intended is how a poll ends up quoting a margin it did not earn.',
    })
  }

  for (const p of rows.filter((it) => it.statusCode === 'LIVE' && !it.closesAt)) {
    out.push({
      id: p.id, ref: p.ref, tone: 'info',
      headline: `${p.title} is in the field with no close date.`,
      detail: `${count(p.collectedCount)} collected against a target of ${count(p.targetSample)}.`,
      action: 'A poll that never closes cannot be behind, cannot be reported as a wave, and quietly '
        + 'accumulates interviews from a population that has moved on. Set a close date.',
    })
  }

  for (const p of rows.filter((it) => it.statusCode === 'IN_REVIEW')) {
    out.push({
      id: p.id, ref: p.ref, tone: 'warn',
      headline: `${p.title} is waiting on a reviewer.`,
      detail: p.ownerName ? `Owned by ${p.ownerName}.` : 'No owner recorded.',
      action: 'Review is the only step here that somebody else has to take. Nothing downstream moves until it does.',
    })
  }

  return out.slice(0, 8)
}
